import React, { ComponentType, useEffect, useRef } from 'react';
import styles from './google-address-autocomplete.module.scss';
import { useGooglePlaces } from './use-google-places.hook';

export interface ParsedAddress {
  streetAndNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

function parsePlace(place: any): ParsedAddress {
  const components: any[] = place.address_components || [];
  const get = (type: string) => components.find((c) => c.types.includes(type));

  const route = get('route')?.long_name || '';
  const streetNumber = get('street_number')?.long_name || '';
  const streetAndNumber = [route, streetNumber].filter(Boolean).join(' ');
  const postalCode = get('postal_code')?.long_name || '';
  const city =
    get('locality')?.long_name ||
    get('postal_town')?.long_name ||
    get('administrative_area_level_1')?.long_name ||
    '';
  const country = get('country')?.short_name || '';

  return { streetAndNumber, postalCode, city, country };
}

interface GoogleAddressAutocompleteProps {
  onSelect: (address: ParsedAddress) => void;
}

export const GoogleAddressAutocomplete: ComponentType<
  GoogleAddressAutocompleteProps
> = ({ onSelect }) => {
  const ready = useGooglePlaces();
  const inputRef = useRef<HTMLInputElement>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!ready || !inputRef.current) return;

    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      fields: ['address_components'],
    });

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      onSelectRef.current(parsePlace(place));
      if (inputRef.current) inputRef.current.value = '';
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [ready]);

  if (!process.env.GOOGLE_PLACES_API_KEY) return null;

  return (
    <div className={styles.host}>
      <span className={styles.label}>Adresse suchen</span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Straße, Stadt..."
        className={styles.input}
        autoComplete="off"
      />
    </div>
  );
};
